import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, BookOpen, PlayCircle, Lock, ArrowUpRight, Sparkles, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { isStudentLoggedIn } from "../lib/studentAuth";

const Course = () => {
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showWaitlist, setShowWaitlist] = useState(false);
    const [wl, setWl] = useState({ first_name: "", last_name: "", email: "", reason: "" });
    const [wlSubmitting, setWlSubmitting] = useState(false);
    const [wlDone, setWlDone] = useState(false);
    const isLogged = isStudentLoggedIn();

    useEffect(() => {
        api.get("/course").then((r) => setCourse(r.data)).catch(() => setCourse(null));
    }, []);

    const mode = course?.mode || "waitlist";
    const isWaitlist = mode === "waitlist";

    const startCheckout = async () => {
        setLoading(true);
        try {
            const origin = window.location.origin;
            const r = await api.post("/payments/checkout/session", {
                package_id: "battlefield-course",
                origin_url: origin,
            });
            if (r.data?.url) window.location.href = r.data.url;
            else toast.error("Checkout couldn't start. Try again in a moment.");
        } catch (e) {
            toast.error("Checkout failed to start. Try again.");
        } finally {
            setLoading(false);
        }
    };

    const handlePrimary = () => {
        if (isWaitlist) setShowWaitlist(true);
        else startCheckout();
    };

    const submitWaitlist = async (e) => {
        e.preventDefault();
        setWlSubmitting(true);
        try {
            const r = await api.post("/course/waitlist", {
                first_name: wl.first_name.trim(),
                last_name: wl.last_name.trim(),
                email: wl.email.trim().toLowerCase(),
                reason: wl.reason.trim(),
            });
            setWlDone(true);
            toast.success(r.data.had_existing_entry ? "You're already on the list — we've updated your info." : "You're on the list. Check your inbox.");
        } catch (e) {
            toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Couldn't save your info. Try again.");
        } finally {
            setWlSubmitting(false);
        }
    };

    const closeWaitlist = () => {
        setShowWaitlist(false);
        // Reset for next open after a short delay (so modal close animates cleanly)
        setTimeout(() => {
            setWl({ first_name: "", last_name: "", email: "", reason: "" });
            setWlDone(false);
        }, 200);
    };

    if (!course) return <div className="min-h-screen bg-black text-white/40 flex items-center justify-center font-stencil tracking-widest text-xs">LOADING...</div>;

    return (
        <div className="bg-black text-white" data-testid="course-page">
            {/* COMING SOON banner (only in waitlist mode) */}
            {isWaitlist && (
                <div className="bg-red-700 text-white text-center py-3 px-6 border-b border-red-900" data-testid="course-coming-soon-banner">
                    <span className="font-stencil text-xs md:text-sm tracking-[0.4em] uppercase">
                        ★ Coming Soon ★ Join the waitlist
                    </span>
                </div>
            )}

            {/* HERO */}
            <section className="relative overflow-hidden border-b border-white/10">
                <div className="absolute inset-0 opacity-30 bg-[radial-gradient(ellipse_at_top,rgba(185,28,28,0.5),transparent_60%)]" />
                <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-24 md:pt-32 pb-20">
                    <div className="grid lg:grid-cols-12 gap-12 items-end">
                        <div className="lg:col-span-8">
                            <div className="flex items-center gap-3 mb-6 text-xs tracking-[0.4em] text-red-600 font-display uppercase">
                                <span className="h-px w-12 bg-red-600" />
                                The Course
                            </div>
                            <h1 className="font-display uppercase tracking-tighter leading-[0.85] text-5xl md:text-7xl lg:text-8xl">
                                {course.title.split(" ").slice(0, -1).join(" ")} <br />
                                <span className="text-red-600">{course.title.split(" ").slice(-1)}.</span>
                            </h1>
                            <p className="mt-8 max-w-2xl text-lg md:text-xl text-white/75 leading-relaxed">
                                {course.subtitle}
                            </p>
                            <p className="mt-4 max-w-2xl text-white/55 leading-relaxed">
                                {course.description}
                            </p>
                            <div className="mt-10 flex flex-wrap items-center gap-4">
                                {isLogged ? (
                                    <Link to="/course/dashboard" className="btn-primary text-base" data-testid="course-go-to-dashboard">
                                        <PlayCircle size={18} /> Open your course
                                    </Link>
                                ) : (
                                    <button onClick={handlePrimary} disabled={loading} className="btn-primary text-base disabled:opacity-50" data-testid="course-enroll-cta">
                                        <Sparkles size={18} /> {isWaitlist ? "Join the waitlist" : (loading ? "Opening checkout..." : `Enroll — $${course.price_usd}`)}
                                    </button>
                                )}
                                <Link to="/course/login" className="text-sm font-display tracking-[0.18em] uppercase text-white/70 hover:text-red-600 transition-colors inline-flex items-center gap-2">
                                    Already enrolled? Log in <ArrowUpRight size={16} />
                                </Link>
                            </div>
                        </div>

                        <div className="lg:col-span-4 lg:pl-6">
                            <div className="bg-[#0a0a0a] border border-red-700 p-8">
                                <div className="font-stencil text-xs tracking-widest text-red-600 mb-3">[ ONE-TIME · LIFETIME ACCESS ]</div>
                                <div className="font-display text-6xl text-white leading-none">${course.price_usd}</div>
                                <ul className="mt-6 space-y-3 text-sm text-white/75">
                                    <li className="flex items-start gap-2"><Check size={16} className="text-red-600 mt-1 flex-shrink-0" /> 4 battlefronts · {course.modules.reduce((s, m) => s + m.lessons.length, 0)} lessons</li>
                                    <li className="flex items-start gap-2"><Check size={16} className="text-red-600 mt-1 flex-shrink-0" /> ~8 minutes per lesson</li>
                                    <li className="flex items-start gap-2"><Check size={16} className="text-red-600 mt-1 flex-shrink-0" /> Workbook PDF for every lesson</li>
                                    <li className="flex items-start gap-2"><Check size={16} className="text-red-600 mt-1 flex-shrink-0" /> Watch anywhere, forever</li>
                                    <li className="flex items-start gap-2"><Check size={16} className="text-red-600 mt-1 flex-shrink-0" /> No subscription, ever</li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* MODULES */}
            <section className="bg-[#050505] py-20 md:py-28 border-b border-white/10">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="flex items-center gap-3 mb-6 text-xs tracking-[0.4em] text-red-600 font-display uppercase">
                        <span className="h-px w-12 bg-red-600" /> What's inside
                    </div>
                    <h2 className="font-display text-4xl md:text-6xl uppercase tracking-tighter leading-[0.9]">
                        Four <span className="text-red-600">battlefronts.</span>
                    </h2>

                    <div className="mt-14 space-y-px bg-white/10 border border-white/10">
                        {course.modules.map((m) => (
                            <div key={m.id} className="bg-black p-6 md:p-8" data-testid={`module-${m.id}`}>
                                <div className="grid md:grid-cols-12 gap-6 items-start">
                                    <div className="md:col-span-4">
                                        <div className="font-display text-7xl text-red-600 leading-none">0{m.number}</div>
                                        <h3 className="mt-3 font-display text-2xl md:text-3xl uppercase tracking-tight">{m.title}</h3>
                                        <p className="mt-2 text-sm text-white/55 italic">{m.subtitle}</p>
                                    </div>
                                    <div className="md:col-span-8 space-y-3">
                                        {m.lessons.map((l) => (
                                            <div key={l.id} className="border border-white/10 p-4 flex items-start gap-4 hover:border-red-700/40 transition-colors">
                                                <div className="bg-red-700/10 border border-red-700/40 p-2 flex-shrink-0">
                                                    <Lock size={14} className="text-red-600" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-stencil text-[10px] tracking-widest text-red-600">LESSON {l.number}</span>
                                                        <span className="font-stencil text-[10px] tracking-widest text-white/30">· {l.duration_min} MIN</span>
                                                    </div>
                                                    <div className="font-display text-lg uppercase tracking-tight mt-1">{l.title}</div>
                                                    <div className="text-sm text-white/55 mt-1">{l.description}</div>
                                                </div>
                                                <BookOpen size={16} className="text-white/30 flex-shrink-0" />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="mt-16 text-center">
                        {!isLogged && (
                            <button onClick={handlePrimary} disabled={loading} className="btn-primary text-base disabled:opacity-50" data-testid="course-enroll-cta-bottom">
                                <Sparkles size={18} /> {isWaitlist ? "Join the waitlist" : (loading ? "Opening checkout..." : `Enroll — $${course.price_usd}`)}
                            </button>
                        )}
                    </div>
                </div>
            </section>

            {/* Waitlist modal */}
            {showWaitlist && (
                <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur flex items-center justify-center p-4" onClick={closeWaitlist} data-testid="course-waitlist-modal">
                    <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-black border border-red-700 p-8 md:p-10 relative">
                        <button onClick={closeWaitlist} className="absolute top-4 right-4 text-white/40 hover:text-red-600" data-testid="course-waitlist-close">
                            <X size={20} />
                        </button>
                        {!wlDone ? (
                            <>
                                <div className="font-stencil text-xs tracking-widest text-red-600 mb-3">[ THE BATTLEFIELD COURSE · WAITLIST ]</div>
                                <h3 className="font-display text-3xl uppercase tracking-tight leading-[0.95]">
                                    Be first <span className="text-red-600">in line.</span>
                                </h3>
                                <p className="mt-3 text-white/65 text-sm leading-relaxed">
                                    Tim is recording the course right now. Drop your info and you'll be the first to know when it goes live. No spam, no pressure.
                                </p>
                                <form onSubmit={submitWaitlist} className="mt-6 space-y-3" data-testid="course-waitlist-form">
                                    <div className="grid grid-cols-2 gap-3">
                                        <input type="text" placeholder="First name" required value={wl.first_name} onChange={(e) => setWl({ ...wl, first_name: e.target.value })} className="w-full px-4 py-3 bg-black border border-white/15 focus:border-red-600 outline-none text-white placeholder-white/30" data-testid="waitlist-first-name" autoComplete="given-name" />
                                        <input type="text" placeholder="Last name" required value={wl.last_name} onChange={(e) => setWl({ ...wl, last_name: e.target.value })} className="w-full px-4 py-3 bg-black border border-white/15 focus:border-red-600 outline-none text-white placeholder-white/30" data-testid="waitlist-last-name" autoComplete="family-name" />
                                    </div>
                                    <input type="email" placeholder="your@email.com" required value={wl.email} onChange={(e) => setWl({ ...wl, email: e.target.value })} className="w-full px-4 py-3 bg-black border border-white/15 focus:border-red-600 outline-none text-white placeholder-white/30" data-testid="waitlist-email" autoComplete="email" />
                                    <textarea placeholder="What's drawing you to this? (Optional — Tim reads every one.)" rows={3} value={wl.reason} onChange={(e) => setWl({ ...wl, reason: e.target.value })} className="w-full px-4 py-3 bg-black border border-white/15 focus:border-red-600 outline-none text-white placeholder-white/30 resize-none" data-testid="waitlist-reason" />
                                    <button type="submit" disabled={wlSubmitting} className="w-full btn-primary justify-center disabled:opacity-50" data-testid="waitlist-submit">
                                        <Sparkles size={16} /> {wlSubmitting ? "Saving..." : "Join the waitlist"}
                                    </button>
                                </form>
                            </>
                        ) : (
                            <div className="text-center py-4" data-testid="course-waitlist-success">
                                <div className="bg-red-700/15 border border-red-700/40 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                                    <Check size={32} className="text-red-600" />
                                </div>
                                <h3 className="font-display text-3xl uppercase tracking-tight">You're <span className="text-red-600">in.</span></h3>
                                <p className="mt-3 text-white/70">Check your inbox — Tim just sent you a note.</p>
                                <button onClick={closeWaitlist} className="mt-8 btn-ghost text-xs" data-testid="waitlist-success-close">
                                    Got it
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Course;
